import { BusinessRounded, Description, MoreVert, Videocam } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { pink } from '@mui/material/colors'
import moment from 'moment'
import { MouseEvent, memo, useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { showStats } from '../../../../redux/actions/libraryStats'
import { toggleCard } from '../../../../redux/actions/multipleSelection'
import { IRootState } from '../../../../redux/types'
import FiletypeIcon from '../../../backoffice/library/ui/components/FiletypeIcon'
import useScreenWidth from '../../hooks/useScreenWidth'
import HiddenDesktop from '../../styles/HiddenDesktop'
import HiddenMobile from '../../styles/HiddenMobile'
import { MediaBox, MediaLabel, VipBlock } from '../../styles/ResourceStyle'
import CustomChip from '../CustomChip'
import TooltipCards from '../TooltipCards'
import FavoriteButton from '../buttons/FavoriteButton'
import ShareIcon from '../icons/ShareIcon'
import Visibility from './Visibility'
import { isAdmin } from '@/contexts/backoffice/users/utils/RolesHelper'
import { updateGeneralSummaryReport } from '@/redux/actions/generalsummaryreport'
import { useIntersectionObserver } from '@/contexts/frontoffice/library/ui/hooks/useIntersectionObserver'

type MediaFileBlockProps = {
  data: any
  vipContent: boolean
  hasaccess: boolean
  handleClick: (id: number, hasaccess: boolean) => void
  handleDownload: (resource_id: number, alias: string, ext: string) => void
  handleFavorite: (state: boolean) => void
  brand: string
  isCatalog?: boolean
  isSelected?: boolean
  onSelect?: (id: number) => void
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
function MediaFileBlock({
  data,
  vipContent,
  hasaccess = false,
  handleClick,
  handleDownload,
  handleFavorite,
  brand,
  isCatalog = false
}: MediaFileBlockProps) {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const dispatch = useDispatch()

  const { showCheckboxes, selectedCards } = useSelector((state: IRootState) => state.multipleSelection)
  const [hasFetchedStats, setHasFetchedStats] = useState(false)
  const [anchorOp, setAnchorOp] = useState<null | HTMLElement>(null)
  const [openOptions, setOpenOptions] = useState(false)
  const [impressionSent, setImpressionSent] = useState(false)

  const cardRef = useRef<HTMLDivElement>(null)

  const handleImpression = useCallback(() => {
    if (impressionSent) {
      return
    }

    const payload = {
      data: [{ menu_id: '2', foreign_id: data.id }],
      action: 'times_displayed'
    }
    dispatch(updateGeneralSummaryReport(payload))
    setImpressionSent(true)
  }, [dispatch, data.id, impressionSent])

  useIntersectionObserver(cardRef, { threshold: 0.5 }, handleImpression)

  const handleCloseOptions = () => {
    setAnchorOp(null)
    setOpenOptions(false)
  }

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
  const TypeFile = () => {
    const fileNameExtension = data.ext === null || data.ext === undefined ? '' : data.ext.toUpperCase()

    const $imageExtensions = ['JPG', 'GIF', 'PNG', 'JPEG', 'BMP', 'TIF']
    const $fileExtensions = ['PDF', 'DOCX', 'XLS', 'XLSX', 'CSV']
    const $videoExtensions = ['MP4', 'MOV', 'WMV', 'AVI']

    if ($imageExtensions.includes(fileNameExtension) || data.cover !== null) {
      const tmpFilename = data.cover === null || data.cover === undefined ? data.file_name : data.cover

      return (
        <MediaBox
          style={{
            background: `${encodeURI(`url(${import.meta.env.VITE_AWS_URL}/resources/webp/${tmpFilename})`)}`
          }}
          title={data.file_name}
        >
          {vipContent && (
            <MediaLabel textAlign="center">
              <Typography variant={isMobile ? 'body1' : 'subtitle1'}>VIP</Typography>
            </MediaLabel>
          )}
        </MediaBox>
      )
    }

    if ($videoExtensions.includes(fileNameExtension)) {
      return (
        <MediaBox>
          <Videocam fontSize="large" color="inherit" />
          {vipContent && (
            <MediaLabel textAlign="center">
              <Typography variant={isMobile ? 'body1' : 'subtitle1'}>VIP</Typography>
            </MediaLabel>
          )}
        </MediaBox>
      )
    }

    if ($fileExtensions.includes(fileNameExtension)) {
      return (
        <MediaBox>
          <Description fontSize="large" color="inherit" />
          {vipContent && (
            <MediaLabel textAlign="center">
              <Typography variant={isMobile ? 'body1' : 'subtitle1'}>VIP</Typography>
            </MediaLabel>
          )}
        </MediaBox>
      )
    }

    return (
      <MediaBox>
        <Description fontSize="large" color="inherit" />
        {vipContent && (
          <MediaLabel textAlign="center">
            <Typography variant={isMobile ? 'body1' : 'subtitle1'}>VIP</Typography>
          </MediaLabel>
        )}
      </MediaBox>
    )
  }

  const isSelectedCard = selectedCards?.includes(data.id)

  const handleSelect = () => {
    dispatch(toggleCard(data.id))
  }

  const dataStats = useSelector((state: IRootState) => state.libraryStats.stats?.[data.id]) ?? null
  const roleId = useSelector((state: IRootState) => state.auth.user?.role_id)

  const handleHoverStats = () => {
    if (!hasFetchedStats) {
      dispatch(showStats('2', data.id))
      setHasFetchedStats(true)
    }
  }

  return (
    <>
      <Grid
        ref={cardRef}
        xs={6}
        sm={4}
        md={useScreenWidth(1000) ? 6 : 4}
        lg={isCatalog ? 4 : 3}
        className="websiteResource"
        component="div"
        sx={{ p: '12px' }}
        maxWidth="50%"
      >
        {isAdmin(roleId) ? (
          <Tooltip
            title={
              dataStats && Object.keys(dataStats).length > 0 ? (
                <Box>
                  <Typography variant="body1">{t('stats')}</Typography>
                  <Typography variant="body2">
                    {t('views')}: {dataStats.times_viewed}
                  </Typography>
                  <Typography variant="body2">
                    {t('clicks')}: {dataStats.times_clicked}
                  </Typography>
                  <Typography variant="body2">
                    {t('impressions')}: {dataStats.times_displayed}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body1">{t('loading')}</Typography>
              )
            }
            placement="top"
            arrow
          >
            <Box onMouseEnter={handleHoverStats}>
              <Card
                sx={[
                  { borderRadius: '8px', border: 'none', cursor: 'pointer' },
                  isCatalog
                    ? {
                        boxShadow: '6px 6px 20px rgb(0 0 0 / 10%)',
                        padding: { xs: 0 }
                      }
                    : {
                        boxShadow: {
                          lg: '0px 0px 6px 1px rgb(0 0 0 / 8%) !important',
                          xl: '0px 0px 6px 1px rgb(0 0 0 / 8%) !important'
                        }
                      },
                  vipContent
                    ? {
                        boxShadow: '0px 4px 32px 0px rgba(230, 185, 128, 0.3) !important'
                      }
                    : {}
                ]}
              >
                <div style={{ position: 'relative' }}>
                  {showCheckboxes && (
                    <Box position={'absolute'} top={8} left={12} zIndex={10}>
                      <FormControlLabel
                        control={<Checkbox checked={isSelectedCard} onChange={handleSelect} style={{ color: pink[400] }} />}
                        label=""
                      />
                    </Box>
                  )}
                  <Button
                    aria-label="cover"
                    fullWidth
                    onClick={() => handleClick(data.id, !(vipContent && !hasaccess))}
                    style={{ padding: 0 }}
                  >
                    <TypeFile />
                  </Button>
                </div>

                <CardContent
                  sx={theme => ({
                    padding: 2,
                    paddingBottom: 0,
                    [theme.breakpoints.up('sm')]: {
                      minHeight: 74
                    },
                    [theme.breakpoints.down('sm')]: {
                      paddingBottom: 0,
                      padding: 1
                    }
                  })}
                >
                  <Box
                    sx={theme => ({
                      overflow: 'hidden',
                      display: 'flex',
                      [theme.breakpoints.up('sm')]: {
                        flexDirection: 'column'
                      },
                      [theme.breakpoints.down('sm')]: {
                        gridGap: 4
                      }
                    })}
                    onClick={() => handleClick(data.id, !(vipContent && !hasaccess))}
                  >
                    <HiddenDesktop>
                      <FiletypeIcon id={data.filetype_id} />
                    </HiddenDesktop>
                    <TooltipCards titleCard={data?.alias ? data.alias : data.file_name} classNameCustom="resourceTitle" />
                    {isCatalog ? (
                      <HiddenMobile>
                        <Typography variant="caption" color="textSecondary">
                          {`${moment(data.date === null ? data.created_at : data.date).format(isMobile ? 'l' : 'LL')}`}
                        </Typography>
                      </HiddenMobile>
                    ) : vipContent ? (
                      <HiddenMobile>
                        <Chip
                          sx={theme => ({
                            backgroundColor: 'none',
                            background: 'linear-gradient(89.97deg, #D5B385 -12.76%, #B49568 113.26%)',
                            maxHeight: 20,
                            borderRadius: 4,
                            [theme.breakpoints.down('sm')]: {
                              marginTop: 24,
                              width: '100%',
                              maxWidth: '100%'
                            }
                          })}
                          label={data.filetype}
                        />
                      </HiddenMobile>
                    ) : (
                      <HiddenMobile>
                        <CustomChip type={2} label={data.filetype} />
                      </HiddenMobile>
                    )}
                  </Box>
                </CardContent>
                <CardActions
                  style={
                    isMobile
                      ? {
                          padding: '0px 6px 0px 0px',
                          justifyContent: 'space-between'
                        }
                      : { padding: '0px 12px', justifyContent: 'space-between' }
                  }
                  disableSpacing
                >
                  <Box display="flex" pl={isMobile ? '6px' : 0}>
                    {handleFavorite && <FavoriteButton handleClick={handleFavorite} favoriteState={Boolean(data.favoriteId)} />}
                    <IconButton
                      id={`icon-button-${data.id}`}
                      className="card-options"
                      aria-label="card-options"
                      aria-controls="icon-menu"
                      aria-haspopup="false"
                      onClick={(event: MouseEvent<HTMLButtonElement>) => {
                        setAnchorOp(event.currentTarget)
                        setOpenOptions(true)
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    gap="2px"
                    onClick={() => handleClick(data.id, !(vipContent && !hasaccess))}
                    pr={1}
                  >
                    {isCatalog ? (
                      <HiddenDesktop>
                        <Typography variant="caption" color="textSecondary">
                          {`${moment(data.date === null ? data.created_at : data.date).format(isMobile ? 'l' : 'LL')}`}
                        </Typography>
                      </HiddenDesktop>
                    ) : (
                      <Visibility variant={isMobile && 5} type={data.visibility} />
                    )}
                    {brand !== undefined && brand !== '' && (
                      <>
                        <HiddenMobile>
                          <Box display="flex" gap="4px">
                            <BusinessRounded fontSize="small" htmlColor="#8F99A3" />
                            <Typography variant="body2" color="textSecondary">
                              {brand}
                            </Typography>
                          </Box>
                        </HiddenMobile>
                        <HiddenDesktop>
                          <Typography variant="caption" color="textSecondary">
                            {brand}
                          </Typography>
                        </HiddenDesktop>
                      </>
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Box>
          </Tooltip>
        ) : (
          <Card
            sx={[
              vipContent
                ? {
                    boxShadow: '0px 4px 32px 0px rgba(230, 185, 128, 0.3) !important'
                  }
                : {},
              isCatalog
                ? {
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '6px 6px 20px rgb(0 0 0 / 10%)',
                    padding: { xs: 0 }
                  }
                : {
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: {
                      lg: '0px 0px 6px 1px rgb(0 0 0 / 8%) !important',
                      xl: '0px 0px 6px 1px rgb(0 0 0 / 8%) !important'
                    }
                  }
            ]}
          >
            <div style={{ position: 'relative' }}>
              {showCheckboxes && (
                <Box position={'absolute'} top={8} left={12} zIndex={10}>
                  <FormControlLabel
                    control={<Checkbox checked={isSelectedCard} onChange={handleSelect} style={{ color: pink[400] }} />}
                    label=""
                  />
                </Box>
              )}
              <Button fullWidth onClick={() => handleClick(data.id, !(vipContent && !hasaccess))} style={{ padding: 0 }}>
                <TypeFile />
              </Button>
            </div>

            <CardContent
              sx={theme => ({
                padding: '12px',
                paddingBottom: '0px',
                [theme.breakpoints.up('sm')]: {
                  minHeight: 74
                },
                [theme.breakpoints.down('sm')]: {
                  paddingBottom: '0px',
                  padding: '8px'
                }
              })}
            >
              <Box
                sx={theme => ({
                  overflow: 'hidden',
                  display: 'flex',
                  [theme.breakpoints.up('sm')]: {
                    flexDirection: 'column'
                  },
                  [theme.breakpoints.down('sm')]: {
                    gap: '4px'
                  }
                })}
                onClick={() => handleClick(data.id, !(vipContent && !hasaccess))}
              >
                <HiddenDesktop>
                  <FiletypeIcon id={data.filetype_id} />
                </HiddenDesktop>
                <TooltipCards titleCard={data?.alias !== null ? data.alias : data.file_name} classNameCustom="resourceTitle" />
                {isCatalog ? (
                  <HiddenMobile>
                    <Typography variant="caption" color="textSecondary">
                      {`${moment(data.date === null ? data.created_at : data.date).format(isMobile ? 'l' : 'LL')}`}
                    </Typography>
                  </HiddenMobile>
                ) : vipContent ? (
                  <HiddenMobile>
                    <Chip
                      sx={theme => ({
                        backgroundColor: 'none',
                        background: 'linear-gradient(89.97deg, #D5B385 -12.76%, #B49568 113.26%)',
                        maxHeight: 20,
                        borderRadius: '4px',
                        [theme.breakpoints.down('sm')]: {
                          marginTop: '24px',
                          width: '100%',
                          maxWidth: '100%'
                        }
                      })}
                      label={data.filetype}
                    />
                  </HiddenMobile>
                ) : (
                  <HiddenMobile>
                    <CustomChip type={2} label={data.filetype} />
                  </HiddenMobile>
                )}
              </Box>
            </CardContent>
            <CardActions
              style={
                isMobile
                  ? {
                      padding: '0px 6px 0px 0px',
                      justifyContent: 'space-between'
                    }
                  : { padding: '0px 12px', justifyContent: 'space-between' }
              }
              disableSpacing
            >
              <Box display="flex" pl={isMobile ? '6px' : 0}>
                {handleFavorite && <FavoriteButton handleClick={handleFavorite} favoriteState={Boolean(data.favoriteId)} />}
                <IconButton
                  id={`icon-button-${data.id}`}
                  className="card-options"
                  aria-controls="icon-menu"
                  aria-haspopup="false"
                  onClick={(event: MouseEvent<HTMLButtonElement>) => {
                    setAnchorOp(event.currentTarget)
                    setOpenOptions(true)
                  }}
                >
                  <MoreVert />
                </IconButton>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-end"
                gap="2px"
                onClick={() => handleClick(data.id, !(vipContent && !hasaccess))}
                pr={1}
              >
                {isCatalog ? (
                  <HiddenDesktop>
                    <Typography variant="caption" color="textSecondary">
                      {`${moment(data.date === null ? data.created_at : data.date).format(isMobile ? 'l' : 'LL')}`}
                    </Typography>
                  </HiddenDesktop>
                ) : (
                  <Visibility variant={isMobile && 5} type={data.visibility} />
                )}
                {brand !== undefined && brand !== '' && (
                  <>
                    <HiddenMobile>
                      <Box display="flex" gap="4px">
                        <BusinessRounded fontSize="small" htmlColor="#8F99A3" />
                        <Typography variant="body2" color="textSecondary">
                          {brand}
                        </Typography>
                      </Box>
                    </HiddenMobile>
                    <HiddenDesktop>
                      <Typography variant="caption" color="textSecondary">
                        {brand}
                      </Typography>
                    </HiddenDesktop>
                  </>
                )}
              </Box>
            </CardActions>
          </Card>
        )}

        {/* Hidden content vip */}
        {vipContent && !hasaccess && (
          <VipBlock alignContent="center" textAlign="center">
            <Typography variant="subtitle1" color="inherit">
              {t('this-is-a-content')}
            </Typography>
            <Typography variant="h5" color="inherit">
              VIP
            </Typography>
          </VipBlock>
        )}
      </Grid>
      <Menu
        id={`simple-menu-${data.id}`}
        anchorEl={anchorOp}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        open={openOptions}
        onClose={handleCloseOptions}
      >
        {handleClick && <MenuItem onClick={() => handleClick(data.id, !(vipContent && !hasaccess))}>{t('view')}</MenuItem>}
        <MenuItem onClick={() => handleDownload(data.id, data?.alias, data?.ext)}>{t('download')}</MenuItem>
        <ShareIcon
          component="menuItem"
          visibility={data.visibility}
          title={data.alias !== null ? data.alias : data.file_name}
          description={data.alias}
          type="resource"
          link={`${window.location.origin}${
            window.location.pathname === '/comercial' ? '/biblioteca' : window.location.pathname
          }/${data.id}${window.location.search}`}
          multimedia={`${import.meta.env.VITE_AWS_URL}/resources/${data.file_name}`}
          multimediaType={data.ext === 'mp4' ? 'video' : 'image'}
          foreignId={data.id}
        />
      </Menu>
    </>
  )
}

export default memo(MediaFileBlock)
